import React, { useEffect, useMemo, useRef } from "react";
import { Stack } from "react-bootstrap";
import { colors } from "src/styles";
import styled from "styled-components";
import * as d3 from "d3";

interface WidgetProps {
  caption: string;
}

interface BudgetData {
  category: string;
  budget: number;
  actual: number;
}

const BudgetToActualChart = ({ caption }: WidgetProps) => {
  const svgRef = useRef<SVGSVGElement>(null);

  // Static data - to be replaced with API data later
  const budgetData: BudgetData[] = useMemo(
    () => [
      { category: "Janitorial", budget: 150000, actual: 125000 },
      { category: "HVAC", budget: 100000, actual: 85000 },
      { category: "Plumbing", budget: 70000, actual: 62000 },
      { category: "Electrical", budget: 65000, actual: 58000 },
      { category: "Landscaping", budget: 50000, actual: 45000 },
      { category: "Other", budget: 80000, actual: 82000 },
    ],
    [],
  );

  const totalBudget = useMemo(() => budgetData.reduce((sum, d) => sum + d.budget, 0), [budgetData]);
  const totalActual = useMemo(() => budgetData.reduce((sum, d) => sum + d.actual, 0), [budgetData]);
  const variance = totalBudget - totalActual;
  const variancePercent = ((variance / totalBudget) * 100).toFixed(1);

  useEffect(() => {
    if (!svgRef.current || budgetData.length === 0) return;

    const margin = { top: 20, right: 100, bottom: 60, left: 120 };
    const width = 800 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Y scale (categories)
    const y = d3
      .scaleBand()
      .domain(budgetData.map((d) => d.category))
      .range([0, height])
      .padding(0.3);

    // X scale (amounts)
    const maxValue = d3.max(budgetData, (d) => Math.max(d.budget, d.actual)) || 0;
    const x = d3
      .scaleLinear()
      .domain([0, maxValue * 1.1])
      .range([0, width]);

    // Add Y axis
    svg.append("g").call(d3.axisLeft(y)).selectAll("text").style("font-size", "12px");

    // Add X axis
    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(
        d3
          .axisBottom(x)
          .ticks(6)
          .tickFormat((d) => `$${(d as number) / 1000}k`),
      )
      .selectAll("text")
      .style("font-size", "12px");

    const barHeight = y.bandwidth() / 2;

    // Add Budget bars with grow-from-left animation
    svg
      .selectAll(".budget-bar")
      .data(budgetData)
      .enter()
      .append("rect")
      .attr("class", "budget-bar")
      .attr("x", 0)
      .attr("y", (d) => y(d.category) || 0)
      .attr("width", 0)
      .attr("height", barHeight)
      .attr("fill", colors.grey.light10)
      .style("opacity", 0.9)
      .on("mouseover", function () {
        d3.select(this).style("opacity", 1).style("cursor", "pointer");
      })
      .on("mouseout", function () {
        d3.select(this).style("opacity", 0.9);
      })
      .transition()
      .duration(700)
      .ease(d3.easeQuadOut)
      .attr("width", (d) => x(d.budget));

    // Add Actual bars with grow-from-left animation
    svg
      .selectAll(".actual-bar")
      .data(budgetData)
      .enter()
      .append("rect")
      .attr("class", "actual-bar")
      .attr("x", 0)
      .attr("y", (d) => (y(d.category) || 0) + barHeight)
      .attr("width", 0)
      .attr("height", barHeight)
      .attr("fill", (d) => (d.actual > d.budget ? "#ed6c02" : colors.kleenway.green))
      .style("opacity", 0.9)
      .on("mouseover", function () {
        d3.select(this).style("opacity", 1).style("cursor", "pointer");
      })
      .on("mouseout", function () {
        d3.select(this).style("opacity", 0.9);
      })
      .transition()
      .duration(700)
      .delay(150)
      .ease(d3.easeQuadOut)
      .attr("width", (d) => x(d.actual));

    // Add Budget value labels
    svg
      .selectAll(".budget-label")
      .data(budgetData)
      .enter()
      .append("text")
      .attr("class", "budget-label")
      .attr("x", (d) => x(d.budget) + 5)
      .attr("y", (d) => (y(d.category) || 0) + barHeight / 2)
      .attr("dy", "0.35em")
      .attr("font-size", "11px")
      .attr("fill", colors.grey.dark)
      .text((d) => `$${(d.budget / 1000).toFixed(0)}k`);

    // Add Actual value labels
    svg
      .selectAll(".actual-label")
      .data(budgetData)
      .enter()
      .append("text")
      .attr("class", "actual-label")
      .attr("x", (d) => x(d.actual) + 5)
      .attr("y", (d) => (y(d.category) || 0) + barHeight + barHeight / 2)
      .attr("dy", "0.35em")
      .attr("font-size", "11px")
      .attr("fill", colors.grey.dark)
      .text((d: { actual: number }) => `$${(d.actual / 1000).toFixed(0)}k`);

    // Add legend
    const legend = svg.append("g").attr("transform", `translate(${width - 80}, -10)`);

    legend
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", 15)
      .attr("height", 15)
      .attr("fill", colors.grey.light10);

    legend
      .append("text")
      .attr("x", 20)
      .attr("y", 12)
      .attr("font-size", "12px")
      .attr("fill", colors.grey.dark)
      .text("Budget");

    legend
      .append("rect")
      .attr("x", 0)
      .attr("y", 20)
      .attr("width", 15)
      .attr("height", 15)
      .attr("fill", colors.kleenway.green);

    legend
      .append("text")
      .attr("x", 20)
      .attr("y", 32)
      .attr("font-size", "12px")
      .attr("fill", colors.grey.dark)
      .text("Actual");

    // Add X axis label
    svg
      .append("text")
      .attr("x", width / 2)
      .attr("y", height + margin.bottom - 10)
      .style("text-anchor", "middle")
      .style("font-size", "12px")
      .style("fill", colors.grey.dark)
      .text("Amount ($)");
  }, [budgetData]);

  return (
    <WidgetContainer>
      <Stack gap={3}>
        <HeaderRow>
          <h1 style={{ fontWeight: 600, margin: 0 }}>{caption}</h1>
          <StaticDataBadge>Static Data</StaticDataBadge>
        </HeaderRow>
        <SummaryRow>
          <SummaryItem>
            <SummaryLabel>Total Budget:</SummaryLabel>
            <SummaryValue>${totalBudget.toLocaleString()}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total Actual:</SummaryLabel>
            <SummaryValue>${totalActual.toLocaleString()}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Variance:</SummaryLabel>
            <SummaryValue $isPositive={variance >= 0}>
              ${Math.abs(variance).toLocaleString()} ({variance >= 0 ? "+" : "-"}
              {Math.abs(parseFloat(variancePercent))}%)
            </SummaryValue>
          </SummaryItem>
        </SummaryRow>
        <ChartContainer>
          <svg ref={svgRef}></svg>
        </ChartContainer>
      </Stack>
    </WidgetContainer>
  );
};

export default BudgetToActualChart;

const WidgetContainer = styled.div`
  display: flex;
  border: 1px solid ${colors.grey.light11};
  border-top: 4px solid ${colors.kleenway.green};
  padding: 16px;
  border-radius: 6px;
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const StaticDataBadge = styled.span`
  background-color: ${colors.grey.light11};
  color: ${colors.grey.dark};
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 24px;
  padding: 12px;
  background-color: ${colors.grey.light3};
  border-radius: 6px;
  flex-wrap: wrap;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryLabel = styled.span`
  font-size: 12px;
  color: ${colors.grey.default};
  font-weight: 500;
`;

const SummaryValue = styled.span<{ $isPositive?: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${({ $isPositive }: { $isPositive?: boolean }) =>
    $isPositive === undefined ? colors.grey.dark : $isPositive ? colors.kleenway.green : "#ed6c02"};
`;
